import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Footer from "@/components/Footer";
import PwaMobileShell from "@/components/PwaMobileShell";
import Landing from "./pages/Landing";
import { implementedGuideSlugs } from "@/data/guides";
import { blogArticles } from "@/data/blogArticles";
import { trackPageView } from "@/lib/analytics";
import SeoMeta from "@/components/SeoMeta";
import SeoStructuredData from "@/components/SeoStructuredData";
import CookieConsentController from "@/components/consent/CookieConsentController";
import AttributionCaptureController from "@/components/AttributionCaptureController";

const queryClient = new QueryClient();
const Access = lazy(() => import("./pages/Access"));
const AdminAccounts = lazy(() => import("./pages/admin/AdminAccounts"));
const AdminAcquisition = lazy(() => import("./pages/admin/AdminAcquisition"));
const AdminArtifacts = lazy(() => import("./pages/admin/AdminArtifacts"));
const AdminCaseTrace = lazy(() => import("./pages/admin/AdminCaseTrace"));
const AdminCases = lazy(() => import("./pages/admin/AdminCases"));
const AdminErrors = lazy(() => import("./pages/admin/AdminErrors"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));
const AdminHealth = lazy(() => import("./pages/admin/AdminHealth"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminLegalQA = lazy(() => import("./pages/admin/AdminLegalQA"));
const AdminMail = lazy(() => import("./pages/admin/AdminMail"));
const AdminOperations = lazy(() => import("./pages/admin/AdminOperations"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminPipelineEvents = lazy(() => import("./pages/admin/AdminPipelineEvents"));
const AdminProvenance = lazy(() => import("./pages/admin/AdminProvenance"));
const AdminQueues = lazy(() => import("./pages/admin/AdminQueues"));
const AdminRecovery = lazy(() => import("./pages/admin/AdminRecovery"));
const AdminRevenue = lazy(() => import("./pages/admin/AdminRevenue"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const CheckoutRecover = lazy(() => import("./pages/CheckoutRecover"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DefenseHub = lazy(() => import("./pages/DefenseHub"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const DuiUnder21Page = lazy(() => import("./pages/DuiUnder21Page"));
const FAQ = lazy(() => import("./pages/FAQ"));
const FightTrafficTicketPage = lazy(() => import("./pages/FightTrafficTicketPage"));
const Guides = lazy(() => import("./pages/Guides"));
const GuideArticle = lazy(() => import("./pages/GuideArticle"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Intake = lazy(() => import("./pages/Intake"));
const ContactSupport = lazy(() => import("./pages/ContactSupport"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const CertifiedMailFilingPage = lazy(() => import("./pages/CertifiedMailFilingPage"));
const ContestSpeedingTicketPage = lazy(() => import("./pages/ContestSpeedingTicketPage"));
const LosAngelesTrafficTicketPage = lazy(() => import("./pages/LosAngelesTrafficTicketPage"));
const MailPrep = lazy(() => import("./pages/MailPrep"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PremiumDefensePage = lazy(() => import("./pages/PremiumDefensePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const RecordsCenter = lazy(() => import("./pages/RecordsCenter"));
const RecordsRequestPage = lazy(() => import("./pages/RecordsRequestPage"));
const RecoverPurchasePage = lazy(() => import("./pages/RecoverPurchasePage"));
const RedLightCameraTicketPage = lazy(() => import("./pages/RedLightCameraTicketPage"));
const RefundPage = lazy(() => import("./pages/RefundPage"));
const Result = lazy(() => import("./pages/Result"));
const SanFranciscoTrafficDefensePage = lazy(() => import("./pages/SanFranciscoTrafficDefensePage"));
const SpeedingTicketCaliforniaPage = lazy(() => import("./pages/SpeedingTicketCaliforniaPage"));
const SpeedingTicketCostCalculator = lazy(() => import("./pages/SpeedingTicketCostCalculator"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const TrafficTicketDismissalPage = lazy(() => import("./pages/TrafficTicketDismissalPage"));
const TR205DefensePage = lazy(() => import("./pages/TR205DefensePage"));
const WrittenContestLandingPage = lazy(() => import("./pages/WrittenContestLandingPage"));
const CvcIntentPage = lazy(() => import("./pages/CvcIntentPage"));
const DevPostOcrIntakeMockup = import.meta.env.DEV
  ? lazy(() => import("./pages/mockups/PostOcrIntakeMockup"))
  : null;
const FloridaMiamiDadeStagingPage = import.meta.env.VITE_FLORIDA_STAGING_ENABLED === "true"
  ? lazy(() => import("./pages/florida-staging/FloridaMiamiDadeStagingPage"))
  : null;

export const RouteAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const SEO_DEFAULT = {
  title: "Page Not Found | JusticeDrive",
  description: "This JusticeDrive page could not be found.",
};

const SEO_ROUTES: Array<{ exact?: string; prefix?: string; title: string; description: string; includeFaqSchema?: boolean }> = [
  { exact: "/", title: "Traffic Ticket Review Before You Pay | JusticeDrive", description: "Upload your traffic ticket for a free initial review of citation facts, evidence gaps and response options in California, Florida, Texas, New York and New Jersey before you decide whether to pay or contest it." },
  { exact: "/new", title: "Case Intake | JusticeDrive", description: "Upload your citation and start structured analysis for California traffic defense." },
  { prefix: "/analysis/", title: "Citation Analysis | JusticeDrive", description: "Review citation analysis, evidence status, and next defense steps." },
  { prefix: "/defense-hub/", title: "Defense Hub | JusticeDrive", description: "Open your case plan, legal breakdown, and document workflow actions." },
  { exact: "/dashboard", title: "My Cases Dashboard | JusticeDrive", description: "Access purchased cases, documents, and next actions tied to your email." },
  { prefix: "/records/", title: "Records Center | JusticeDrive", description: "Generate and review records request drafts for supporting evidence." },
  { prefix: "/mail/", title: "Mail Packet | JusticeDrive", description: "Prepare mail packet workflow and check provider delivery status." },
  { exact: "/access", title: "Access Your Cases | JusticeDrive", description: "Enter your email for secure magic-link access to saved cases." },
  { exact: "/checkout/recover", title: "Resume Your Review | JusticeDrive", description: "Securely resume a saved JusticeDrive checkout and case review." },
  { exact: "/recover", title: "Recover Purchase | JusticeDrive", description: "Recover purchased access by checkout email with a secure magic link." },
  { exact: "/terms", title: "Terms of Service | JusticeDrive", description: "Read JusticeDrive terms, service scope, and legal responsibility boundaries." },
  { exact: "/privacy", title: "Privacy Policy | JusticeDrive", description: "Learn how JusticeDrive collects, uses, and protects citation and account data." },
  { exact: "/cookie-policy", title: "Cookie Policy | JusticeDrive", description: "Learn how JusticeDrive uses necessary, analytics, and advertising technologies with consent controls." },
  { exact: "/refund", title: "Refund Policy | JusticeDrive", description: "Review refund eligibility for one-time JusticeDrive product purchases." },
  { exact: "/disclaimer", title: "Legal Disclaimer | JusticeDrive", description: "JusticeDrive is not a law firm and does not provide legal advice." },
  { exact: "/help", title: "Help Center | JusticeDrive", description: "Find help for analysis, purchases, records drafts, mail workflow, and account recovery." },
  { exact: "/contact", title: "Contact Support | JusticeDrive", description: "Contact JusticeDrive support for purchase, access, or delivery-status issues." },
  { exact: "/faq", title: "FAQ | JusticeDrive", description: "Frequently asked questions about products, access recovery, and legal limitations.", includeFaqSchema: true },
  { exact: "/guides", title: "California Traffic Ticket Guides & Tools | JusticeDrive", description: "Clear explanations and practical tools for understanding California traffic citations, court procedures and response options." },
  { exact: "/blog", title: "Traffic Ticket Resources for CA, FL, TX, NY & NJ | JusticeDrive", description: "Traffic ticket guides and official-source explainers for California, Florida, Texas, New York and New Jersey, including citation review, evidence, court procedures and next steps." },
  { exact: "/speeding-ticket-cost-calculator", title: "California Speeding Ticket Cost Calculator (2026) | JusticeDrive", description: "Estimate the possible cost of a California speeding ticket, including court fees and DMV consequences, then review your citation before you pay." },
  { exact: "/guides/california-speeding-ticket-cost", title: "How Much Is a Speeding Ticket in California? | JusticeDrive", description: "Learn how California speeding-ticket costs can include base fines, assessments, DMV consequences and court-specific amounts." },
  { exact: "/guides/how-to-fight-a-speeding-ticket-in-california", title: "How to Fight a Speeding Ticket in California | JusticeDrive", description: "Learn how California speeding tickets can be reviewed, what evidence may matter, and which response options drivers commonly compare." },
  { exact: "/guides/trial-by-written-declaration-california", title: "Trial by Written Declaration California | JusticeDrive", description: "Learn how California Trial by Written Declaration works, what TR-205 is, and what drivers should check before preparing a written response." },
  { exact: "/trial-by-written-declaration-california", title: "Contest a California Traffic Ticket by Written Declaration | JusticeDrive", description: "Review a California traffic citation, organize evidence, and prepare before following your court's written-declaration instructions." },
  { exact: "/tr-205-california", title: "California TR-205 Preparation Guide | JusticeDrive", description: "Learn what California form TR-205 is, what to gather, and how JusticeDrive can help organize citation and statement preparation." },
  { exact: "/mycitations-online-trial-by-declaration", title: "MyCitations Online Trial by Declaration California | JusticeDrive", description: "Using MyCitations? Review your citation and prepare before submitting through an available California court process." },
  { exact: "/california-vehicle-code/22350", title: "CVC 22350 California Basic Speed Law | JusticeDrive", description: "Understand a California Vehicle Code 22350 Basic Speed Law citation, what conditions to review, and how to start a free citation review." },
  { exact: "/california-vehicle-code/22349-a", title: "CVC 22349(a) California 65 MPH Speed Law | JusticeDrive", description: "Understand a California Vehicle Code 22349(a) citation, the statewide maximum-speed rule, and what citation details to verify." },
  { exact: "/california-vehicle-code/22349-b", title: "CVC 22349(b) California 55 MPH Speed Law | JusticeDrive", description: "Understand a California Vehicle Code 22349(b) citation, the applicable two-lane highway rule, and what roadway details to review." },
  { exact: "/california-vehicle-code/21453-a", title: "CVC 21453(a) California Red Signal Citation | JusticeDrive", description: "Understand a California Vehicle Code 21453(a) red-signal citation and the citation or evidence details to review before responding." },
  { exact: "/california-vehicle-code/21461-a", title: "CVC 21461(a) California Traffic Sign Violation | JusticeDrive", description: "Understand a California Vehicle Code 21461(a) citation, the traffic-control device allegation, and what location details to verify." },
  { exact: "/guides/how-to-look-up-california-traffic-ticket", title: "How to Look Up Your California Traffic Ticket | JusticeDrive", description: "Learn how to find a California traffic ticket, citation number or case status before choosing how to respond." },
  { exact: "/guides/cvc-21461-a-violation-california", title: "CVC 21461(a) Violation Explained | JusticeDrive", description: "Understand what a California Vehicle Code 21461(a) citation may involve and what details to verify." },
  { exact: "/guides/california-traffic-ticket-points", title: "California Traffic Ticket Points | JusticeDrive", description: "Understand how California DMV points may relate to traffic convictions, traffic school, insurance risk and response decisions." },
  { exact: "/guides/speeding-over-100-mph-california", title: "Speeding Over 100 MPH California | JusticeDrive", description: "Learn why California speeding-over-100 allegations require careful review of the citation, speed evidence, court notice and possible DMV consequences." },
  { exact: "/guides/radar-and-lidar-evidence-california", title: "Radar and LiDAR Evidence California | JusticeDrive", description: "Learn how radar and LiDAR evidence may be reviewed in California speeding-ticket cases and what records may matter." },
  { exact: "/product/tr-205-defense", title: "TR-205 Defense Package | JusticeDrive", description: "Trial by Written Declaration workflow with court-ready draft support." },
  { exact: "/product/records-request", title: "Records Request Workflow | JusticeDrive", description: "Generate records request drafts to support citation defense review." },
  { exact: "/product/certified-mail-filing", title: "Certified Mail Filing | JusticeDrive", description: "Prepare and track mail packet workflow through provider-confirmed states." },
  { exact: "/product/premium-defense", title: "Premium Defense Package | JusticeDrive", description: "Full analysis, court PDF, records workflow, and mail workflow access." },
  { exact: "/california/los-angeles-ticket", title: "Los Angeles Traffic Ticket Help | JusticeDrive", description: "Guidance and workflows for responding to traffic tickets in Los Angeles." },
  { exact: "/california/san-francisco-traffic-defense", title: "San Francisco Traffic Ticket Defense Help | JusticeDrive", description: "San Francisco citation defense workflow and evidence preparation guidance." },
  { exact: "/violation/red-light", title: "Red Light Camera Ticket Help | JusticeDrive", description: "Understand red light camera records and defense-document workflow in California." },
  { exact: "/violation/dui-under-21", title: "DUI Under 21 Citation Help | JusticeDrive", description: "Under-21 alcohol-related citation workflow with caution and records guidance." },
  { exact: "/violation/speeding-ticket-california", title: "Speeding Ticket Help California | JusticeDrive", description: "Speeding-ticket workflow with records checklist and defense preparation support." },
  { exact: "/help/contest-speeding-ticket-ca", title: "How to Contest a Speeding Ticket in California | JusticeDrive", description: "Structured steps and evidence considerations for contesting a California speeding citation." },
  { exact: "/help/traffic-ticket-dismissal-ca", title: "Traffic Ticket Dismissal California | JusticeDrive", description: "What dismissal may depend on and how to prepare supporting documentation." },
  { exact: "/help/fight-traffic-ticket-ca", title: "Fight Traffic Ticket California | JusticeDrive", description: "Start with citation facts, records, and trial-by-declaration workflow guidance." },
];
for (const article of blogArticles) {
  SEO_ROUTES.push({
    exact: `/blog/${article.slug}`,
    title: `${article.title} | JusticeDrive`,
    description: article.excerpt,
  });
}
if (FloridaMiamiDadeStagingPage) {
  // Only present in the compiled bundle when VITE_FLORIDA_STAGING_ENABLED is
  // statically true at build time (same guard as the lazy route below), so a
  // normal production build tree-shakes this string data out entirely.
  SEO_ROUTES.push({
    exact: "/staging/florida/miami-dade",
    title: "Miami-Dade Citation Information & Official Options — Internal Staging, Not Public",
    description: "Internal staging-only Miami-Dade civil-traffic screening tool. Not available to the public. Florida rollout has not been approved.",
  });
}

const normalizePathname = (path: string) => {
  if (!path || path === "/") {
    return "/";
  }
  return path.replace(/\/+$/, "");
};

const withTrailingSlash = (path: string) => {
  if (path === "/") {
    return "/";
  }
  return `${normalizePathname(path)}/`;
};

const resolveSeo = (path: string) => {
  const normalizedPath = normalizePathname(path);
  const exactMatch = SEO_ROUTES.find((route) => route.exact === normalizedPath);
  if (exactMatch) {
    return {
      title: exactMatch.title,
      description: exactMatch.description,
      includeFaqSchema: Boolean(exactMatch.includeFaqSchema),
      isKnownRoute: true,
    };
  }
  const prefixMatch = SEO_ROUTES.find((route) => route.prefix && normalizedPath.startsWith(route.prefix));
  if (prefixMatch) {
    return {
      title: prefixMatch.title,
      description: prefixMatch.description,
      includeFaqSchema: Boolean(prefixMatch.includeFaqSchema),
      isKnownRoute: true,
    };
  }
  return { ...SEO_DEFAULT, includeFaqSchema: false, isKnownRoute: false };
};

const BREADCRUMB_LABELS: Record<string, string> = {
  access: "Access",
  recover: "Recover Purchase",
  dashboard: "Dashboard",
  help: "Help",
  contact: "Contact",
  faq: "FAQ",
  product: "Product",
  california: "California",
  violation: "Violation",
  terms: "Terms",
  privacy: "Privacy",
  "cookie-policy": "Cookie Policy",
  refund: "Refund",
  disclaimer: "Disclaimer",
  "tr-205-defense": "TR-205 Defense",
  "records-request": "Records Request",
  "certified-mail-filing": "Certified Mail Filing",
  "premium-defense": "Premium Defense",
  "los-angeles-ticket": "Los Angeles Ticket Help",
  "san-francisco-traffic-defense": "San Francisco Ticket Defense",
  "red-light": "Red Light Camera Ticket",
  "dui-under-21": "DUI Under 21",
  "speeding-ticket-california": "Speeding Ticket California",
  "contest-speeding-ticket-ca": "Contest Speeding Ticket CA",
  "traffic-ticket-dismissal-ca": "Traffic Ticket Dismissal CA",
  "fight-traffic-ticket-ca": "Fight Traffic Ticket CA",
  "trial-by-written-declaration-ca": "Trial by Written Declaration CA",
  guides: "Guides",
  blog: "Blog",
  "speeding-ticket-cost-calculator": "Speeding Ticket Cost Calculator",
  "california-speeding-ticket-cost": "California Speeding Ticket Cost",
  "how-to-fight-a-speeding-ticket-in-california": "How to Fight a Speeding Ticket in California",
  "trial-by-written-declaration-california": "Trial by Written Declaration California",
  "tr-205-california": "California TR-205",
  "mycitations-online-trial-by-declaration": "MyCitations Online Trial by Declaration",
  "california-vehicle-code": "California Vehicle Code",
  "22350": "CVC 22350",
  "22349-a": "CVC 22349(a)",
  "22349-b": "CVC 22349(b)",
  "21453-a": "CVC 21453(a)",
  "21461-a": "CVC 21461(a)",
  "how-to-look-up-california-traffic-ticket": "Look Up California Traffic Ticket",
  "cvc-21461-a-violation-california": "CVC 21461(a) Violation",
  "california-traffic-ticket-points": "California Traffic Ticket Points",
  "speeding-over-100-mph-california": "Speeding Over 100 MPH California",
  "radar-and-lidar-evidence-california": "Radar and LiDAR Evidence California",
};

const buildBreadcrumbs = (pathname: string) => {
  if (pathname.startsWith("/california-vehicle-code/")) {
    const code = pathname.split("/").filter(Boolean).pop() ?? "";
    return [
      { name: "Home", path: "/" },
      { name: BREADCRUMB_LABELS[code] ?? code, path: pathname },
    ];
  }
  const parts = pathname.split("/").filter(Boolean);
  const breadcrumbs: Array<{ name: string; path: string }> = [{ name: "Home", path: "/" }];
  let currentPath = "";
  for (const part of parts) {
    currentPath += `/${part}`;
    breadcrumbs.push({
      name:
        BREADCRUMB_LABELS[part] ??
        part
          .split("-")
          .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
          .join(" "),
      path: currentPath,
    });
  }
  return breadcrumbs;
};

const NOINDEX_PREFIXES = ["/admin", "/analysis/", "/defense-hub/", "/records/", "/mail/", "/checkout/", "/dev/", "/staging/"];
const NOINDEX_EXACT = new Set(["/new", "/access", "/recover", "/dashboard"]);
const INDEXABLE_GUIDE_PATHS = new Set(implementedGuideSlugs.map((slug) => `/guides/${slug}`));

const isPrivateOrNoindexPath = (path: string) => {
  const normalizedPath = normalizePathname(path);
  return (
    NOINDEX_EXACT.has(normalizedPath) ||
    NOINDEX_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix)) ||
    (normalizedPath.startsWith("/guides/") && !INDEXABLE_GUIDE_PATHS.has(normalizedPath))
  );
};

const resolveRobots = (path: string, isKnownRoute: boolean): string =>
  isPrivateOrNoindexPath(path) || !isKnownRoute
    ? "noindex,nofollow"
    : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

export const shouldRenderPublicChrome = (pathname: string) => !pathname.startsWith("/admin") && !pathname.startsWith("/staging/");

const ClearSeoStructuredData = () => {
  useEffect(() => {
    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((node) => node.remove());
  }, []);

  return null;
};

const RouteSeo = () => {
  const location = useLocation();
  const seo = resolveSeo(location.pathname);
  const normalizedPath = normalizePathname(location.pathname);
  const breadcrumbs = buildBreadcrumbs(normalizedPath);
  const robots = resolveRobots(normalizedPath, seo.isKnownRoute);
  const isIndexable = robots.startsWith("index");
  const canonicalPath = isIndexable ? withTrailingSlash(normalizedPath) : null;
  return (
    <>
      <SeoMeta canonicalPath={canonicalPath} description={seo.description} robots={robots} title={seo.title} />
      {isIndexable ? (
        <SeoStructuredData
          canonicalPath={canonicalPath ?? "/"}
          description={seo.description}
          title={seo.title}
          breadcrumbs={breadcrumbs}
          includeFaqSchema={seo.includeFaqSchema}
        />
      ) : (
        <ClearSeoStructuredData />
      )}
    </>
  );
};

const AppChrome = () => {
  const location = useLocation();
  if (!shouldRenderPublicChrome(location.pathname)) {
    return null;
  }
  return (
    <>
      <PwaMobileShell />
      {!location.pathname.startsWith("/analysis/") ? <Footer /> : null}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteAnalytics />
        <AttributionCaptureController />
        <RouteSeo />
        <Suspense fallback={null}>
          <Routes>
            <Route element={<Landing />} path="/" />
            <Route element={<Intake />} path="/new" />
            <Route element={<Result />} path="/analysis/:ticketId" />
            <Route element={<CheckoutSuccess />} path="/checkout/success" />
            <Route element={<CheckoutCancel />} path="/checkout/cancel" />
            <Route element={<CheckoutRecover />} path="/checkout/recover" />
            <Route element={<DefenseHub />} path="/defense-hub/:ticketId" />
            <Route element={<RecordsCenter />} path="/records/:ticketId" />
            <Route element={<MailPrep />} path="/mail/:ticketId" />
            <Route element={<Dashboard />} path="/dashboard" />
            <Route element={<Access />} path="/access" />
            <Route element={<RecoverPurchasePage />} path="/recover" />
            <Route element={<TermsPage />} path="/terms" />
            <Route element={<PrivacyPage />} path="/privacy" />
            <Route element={<CookiePolicyPage />} path="/cookie-policy" />
            <Route element={<RefundPage />} path="/refund" />
            <Route element={<DisclaimerPage />} path="/disclaimer" />
            <Route element={<HelpCenter />} path="/help" />
            <Route element={<ContactSupport />} path="/contact" />
            <Route element={<FAQ />} path="/faq" />
            <Route element={<Guides />} path="/guides" />
            <Route element={<Blog />} path="/blog" />
            <Route element={<BlogArticle />} path="/blog/:slug" />
            <Route element={<SpeedingTicketCostCalculator />} path="/speeding-ticket-cost-calculator" />
            <Route element={<GuideArticle />} path="/guides/:slug" />
            <Route element={<TR205DefensePage />} path="/product/tr-205-defense" />
            <Route element={<WrittenContestLandingPage type="tbwd" />} path="/trial-by-written-declaration-california" />
            <Route element={<WrittenContestLandingPage type="tr205" />} path="/tr-205-california" />
            <Route element={<WrittenContestLandingPage type="mycitations" />} path="/mycitations-online-trial-by-declaration" />
            <Route element={<CvcIntentPage />} path="/california-vehicle-code/:code" />
            <Route element={<RecordsRequestPage />} path="/product/records-request" />
            <Route element={<CertifiedMailFilingPage />} path="/product/certified-mail-filing" />
            <Route element={<PremiumDefensePage />} path="/product/premium-defense" />
            <Route element={<LosAngelesTrafficTicketPage />} path="/california/los-angeles-ticket" />
            <Route element={<SanFranciscoTrafficDefensePage />} path="/california/san-francisco-traffic-defense" />
            <Route element={<RedLightCameraTicketPage />} path="/violation/red-light" />
            <Route element={<DuiUnder21Page />} path="/violation/dui-under-21" />
            <Route element={<SpeedingTicketCaliforniaPage />} path="/violation/speeding-ticket-california" />
            <Route element={<ContestSpeedingTicketPage />} path="/help/contest-speeding-ticket-ca" />
            <Route element={<TrafficTicketDismissalPage />} path="/help/traffic-ticket-dismissal-ca" />
            <Route element={<FightTrafficTicketPage />} path="/help/fight-traffic-ticket-ca" />
            <Route
              element={<Navigate replace to="/guides/trial-by-written-declaration-california/" />}
              path="/help/trial-by-written-declaration-ca"
            />
            {DevPostOcrIntakeMockup ? <Route element={<DevPostOcrIntakeMockup />} path="/dev/mockups/post-ocr-v2" /> : null}
            {FloridaMiamiDadeStagingPage ? (
              <Route element={<FloridaMiamiDadeStagingPage />} path="/staging/florida/miami-dade" />
            ) : null}
            <Route element={<AdminLayout />} path="/admin">
              <Route element={<AdminOverview />} index />
              <Route element={<AdminAcquisition />} path="acquisition" />
              <Route element={<AdminFunnel />} path="funnel" />
              <Route element={<AdminRevenue />} path="revenue" />
              <Route element={<AdminRecovery />} path="recovery" />
              <Route element={<AdminOperations />} path="operations" />
              <Route element={<AdminQueues />} path="queues" />
              <Route element={<AdminCases />} path="cases" />
              <Route element={<AdminCaseTrace />} path="cases/:ticketId" />
              <Route element={<AdminCaseTrace />} path="trace/:ticketId" />
              <Route element={<AdminPipelineEvents />} path="events" />
              <Route element={<AdminLegalQA />} path="legal-qa" />
              <Route element={<AdminProvenance />} path="provenance" />
              <Route element={<AdminErrors />} path="errors" />
              <Route element={<AdminPayments />} path="payments" />
              <Route element={<AdminMail />} path="mail" />
              <Route element={<AdminArtifacts />} path="artifacts" />
              <Route element={<AdminAccounts />} path="accounts" />
              <Route element={<AdminHealth />} path="health" />
            </Route>

            <Route element={<Navigate replace to="/new" />} path="/intake" />
            <Route element={<Navigate replace to="/checkout/success" />} path="/checkout-success" />
            <Route element={<Navigate replace to="/checkout/cancel" />} path="/checkout-cancel" />
            <Route element={<Navigate replace to="/new" />} path="/result" />
            <Route element={<Navigate replace to="/new" />} path="/defense-hub" />
            <Route element={<Navigate replace to="/new" />} path="/mail-prep" />

            <Route element={<NotFound />} path="*" />
          </Routes>
        </Suspense>
        <AppChrome />
        <CookieConsentController />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
